<template>
  <page-hero
    :crumbs="[
      { label: t('Home'), href: route('index') },
      { label: label },
      { label: title }
    ]"
    :title="title"
    :meta="heroMeta"
  />

  <section class="legal-content-section">
    <div class="container">
      <div class="row">
        <div class="col-lg-10">
          <div class="legal-content prose" data-aos="fade-up">
            <slot />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The legal shell. The panel and the prose scale come from the global .prose
   partial, which is the same one the blog post body and the automation preview
   use, so the two can no longer drift. Only the legal list treatment and the
   intro line are declared here. */
.legal-content-section {
  background: $ground-tint;
}

.legal-content {
  :deep(h2),
  :deep(h3) {
    margin-top: 2.5rem;

    &:first-child {
      margin-top: 0;
    }
  }

  :deep(h2) {
    font-size: 1.35rem;
  }

  :deep(h3) {
    font-size: 1.1rem;
    margin-top: 1.75rem;
  }

  /* Clauses are a numbered ledger and a dotted list, not the disc and decimal
     lists the prose partial sets for authored content. */
  :deep(ul),
  :deep(ol) {
    list-style: none !important;
    margin-bottom: 1.25rem;
    padding-left: 0;
  }

  :deep(ul > li),
  :deep(ol > li) {
    display: block;
    margin-bottom: 0.65rem;
    padding-left: 1.5rem;
    position: relative;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(ul > li::before) {
    background: currentColor;
    border-radius: 50%;
    content: '';
    height: 0.35rem;
    left: 0.35rem;
    position: absolute;
    top: 0.72em;
    width: 0.35rem;
  }

  :deep(ol) {
    counter-reset: legal-counter;
  }

  :deep(ol > li) {
    counter-increment: legal-counter;
  }

  :deep(ol > li::before) {
    color: currentColor;
    content: counter(legal-counter) '.';
    font-weight: 500;
    left: 0;
    position: absolute;
    top: 0;
  }

  :deep(ol.legal-list-alpha > li::before) {
    content: '(' counter(legal-counter, lower-alpha) ')';
  }

  :deep(p + ul),
  :deep(p + ol) {
    margin-top: -0.5rem;
  }

  :deep(.legal-intro) {
    color: $text-strong;
    font-size: 1.05rem;
    margin-bottom: 2rem;
  }
}
</style>

<script>
import { useI18n } from 'vue-i18n';
import PageHero from '@/Components/PageHero.vue';

export default {
  components: {
    PageHero
  },

  props: {
    label: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    lastUpdated: {
      type: String,
      default: ''
    }
  },

  computed: {
    /* The publisher and the date share one hero line, the same way the blog
       post pairs its date and author (Blog/Show.vue): a middle dot between the
       parts, with nothing trailing when a part is missing. Keeping the join
       here means both legal pages cannot disagree about the shape. */
    heroMeta() {
      const parts = [];

      if (this.subtitle) {
        parts.push(this.subtitle);
      }

      if (this.lastUpdated) {
        parts.push(`${this.t('Last updated')}: ${this.lastUpdated}`);
      }

      return parts.join(' \u00b7 ');
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Last updated': 'Last updated'
        },
        'zh-MY': {
          'Last updated': '最后更新'
        },
        'ms-MY': {
          'Last updated': 'Kemas kini terakhir'
        }
      }
    });

    return { t };
  }
};
</script>
